/* DraftTemplates — /admin/draft-templates
   Full-screen visual PDF mapper (PDF.js canvas + field overlays) matching old FE.
   DOCX templates get a rendered preview with highlighted merge-fields.
   Field mapping supports: database, system, custom_sql, conditional, user_context, manual_input, ignore. */
import { useState, useRef, useEffect, useCallback } from 'react';
import { renderAsync } from 'docx-preview';
import css from '../AdminPage.module.css';
import {
  useDraftTemplates,
  useDraftTemplate,
  useCreateDraftTemplate,
  useDeleteDraftTemplate,
  useDuplicateDraftTemplate,
  useUpdateDraftTemplate,
  useRescanDraftTemplate,
  useSaveDraftFieldMappings,
  useGenerateDraftTest,
  useBLPFieldDefinitions,
  useDocumentCategories,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import { CONFIG } from '../../../config';
import type { DraftTemplate, DetectedField, FieldMappingConfig } from '../../../types/admin';

/* ── Type definitions ── */
type PdfPageViewport = { width: number; height: number };
type PdfPage = {
  getViewport(options: { scale: number }): PdfPageViewport;
  render(options: { canvasContext: CanvasRenderingContext2D | null; viewport: PdfPageViewport }): {
    promise: Promise<void>;
  };
};
type PdfDocument = {
  numPages: number;
  getPage(num: number): Promise<PdfPage>;
};
type PdfjsLib = {
  getDocument(options: { data: ArrayBuffer }): { promise: Promise<PdfDocument> };
  GlobalWorkerOptions?: { workerSrc?: string };
};

/* ── Source type options ── */
const SOURCE_TYPES: { value: FieldMappingConfig['source_type']; label: string }[] = [
  { value: 'database', label: 'Database Field' },
  { value: 'system', label: 'System Field' },
  { value: 'custom_sql', label: 'Custom SQL' },
  { value: 'conditional', label: 'Conditional' },
  { value: 'user_context', label: 'User Context' },
  { value: 'manual_input', label: 'Manual Input' },
  { value: 'ignore', label: 'Ignore (blank)' },
];

const SYSTEM_FIELDS = [
  { value: 'today_date', label: "Today's Date (DD/MM/YYYY)" },
  { value: 'today_date_long', label: "Today's Date (Long)" },
  { value: 'current_day', label: 'Current Day (number)' },
  { value: 'current_month', label: 'Current Month (number)' },
  { value: 'current_year', label: 'Current Year (number)' },
  { value: 'date_plus_7', label: 'Today + 7 days' },
  { value: 'date_plus_14', label: 'Today + 14 days' },
  { value: 'date_plus_28', label: 'Today + 28 days' },
];

const USER_CONTEXT_FIELDS = [
  { value: 'full_name', label: 'Full Name' },
  { value: 'position_in_company', label: 'Position' },
  { value: 'user_email', label: 'Email Address' },
  { value: 'link_to_signiture', label: 'Signature URL' },
];

const FORMAT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'title_case', label: 'Title Case' },
  { value: 'upper_case', label: 'UPPER CASE' },
  { value: 'lower_case', label: 'lower case' },
  { value: 'currency', label: 'Currency' },
  { value: 'date_uk', label: 'Date (DD/MM/YYYY)' },
  { value: 'date_compact', label: 'Date (DDMMYYYY no separators)' },
  { value: 'date_long', label: 'Date (Long)' },
  { value: 'date_day', label: 'Date → Day only' },
  { value: 'date_month', label: 'Date → Month only (number)' },
  { value: 'date_year', label: 'Date → Year only' },
];

/* ── Helper: normalise mapping value from DB (could be string or object) ── */
function normMapping(raw: FieldMappingConfig | string | undefined): FieldMappingConfig {
  if (!raw) return { source_type: 'database' };
  if (typeof raw === 'string') return { source_type: 'database', field_key: raw };
  return { ...raw, source_type: raw.source_type || 'database' };
}

function isMappingConfigured(m: FieldMappingConfig): boolean {
  switch (m.source_type) {
    case 'database':
      return !!(m.field_key || m.field_definition_id);
    case 'system':
      return !!m.system_field;
    case 'custom_sql':
      return !!m.custom_sql?.trim();
    case 'conditional':
      return !!(m.rules && m.rules.length > 0);
    case 'user_context':
      return !!m.user_field;
    case 'manual_input':
      return true;
    case 'ignore':
      return true;
    default:
      return false;
  }
}

/* ────────────────────────────────────────── */

export default function DraftTemplates() {
  const { data: templates = [], isLoading } = useDraftTemplates();
  const createTemplate = useCreateDraftTemplate();
  const deleteTemplate = useDeleteDraftTemplate();
  const duplicateTemplate = useDuplicateDraftTemplate();
  const updateTemplate = useUpdateDraftTemplate();
  const rescanTemplate = useRescanDraftTemplate();
  const saveFieldMappings = useSaveDraftFieldMappings();
  const generateTest = useGenerateDraftTest();
  const { data: fieldDefs = [] } = useBLPFieldDefinitions();
  const { data: docCategories = [] } = useDocumentCategories();
  const { showToast } = useToast();

  /* ── List state ── */
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  /* ── Add modal ── */
  const [showAddModal, setShowAddModal] = useState(false);
  const [tName, setTName] = useState('');
  const [tDisplay, setTDisplay] = useState('');
  const [tCategory, setTCategory] = useState('');
  const [tDesc, setTDesc] = useState('');
  const [tFile, setTFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* ── Edit modal ── */
  const [editMetaId, setEditMetaId] = useState<number | null>(null);
  const [editDisplay, setEditDisplay] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editTab, setEditTab] = useState<'general' | 'ledger'>('general');
  const [editCategoryId, setEditCategoryId] = useState<number | ''>('');
  const [editMarkForPrinting, setEditMarkForPrinting] = useState(false);
  const [editNamingConvention, setEditNamingConvention] = useState('');

  /* ── Mapper modal ── */
  const [editingId, setEditingId] = useState<number | null>(null);
  const { data: fullTemplate } = useDraftTemplate(editingId);

  /* ── Test gen ── */
  const [testCaseRef, setTestCaseRef] = useState('');

  /* ── Field mapping state ── */
  const [mappings, setMappings] = useState<Record<string, FieldMappingConfig>>({});
  const [selectedField, setSelectedField] = useState<string | null>(null);

  // When fullTemplate loads (or changes), init mappings state
  useEffect(() => {
    if (!fullTemplate) return;
    const fields = fullTemplate.detected_fields ?? [];
    const saved = fullTemplate.field_mappings ?? {};
    const m: Record<string, FieldMappingConfig> = {};
    fields.forEach((f) => {
      const name = typeof f === 'string' ? f : f.name;
      m[name] = normMapping(saved[name]);
    });
    Object.entries(saved).forEach(([k, v]) => {
      if (!m[k]) m[k] = normMapping(v);
    });
    setMappings(m);
    setSelectedField(null);
  }, [fullTemplate]);

  /* ── Filtering ── */
  const categories = ['all', ...new Set(templates.map((t) => t.category || 'General'))];
  const filtered = templates.filter((t) => {
    if (activeCategory !== 'all' && (t.category || 'General') !== activeCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (t.name || '').toLowerCase().includes(q) ||
        (t.display_name || '').toLowerCase().includes(q) ||
        (t.category || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  /* ── Handlers ── */
  function openAdd() {
    setTName('');
    setTDisplay('');
    setTCategory('');
    setTDesc('');
    setTFile(null);
    setShowAddModal(true);
  }

  async function handleAdd() {
    const fd = new FormData();
    fd.append('name', tName);
    if (tDisplay) fd.append('display_name', tDisplay);
    if (tCategory) fd.append('category', tCategory);
    if (tDesc) fd.append('description', tDesc);
    if (tFile) fd.append('file', tFile);
    try {
      await createTemplate.mutateAsync(fd);
      showToast('Template created', 'success');
      setShowAddModal(false);
    } catch {
      showToast('Failed to create template', 'error');
    }
  }

  async function handleDelete(e: React.MouseEvent, id: number) {
    e.stopPropagation();
    if (!confirm('Delete this template?')) return;
    try {
      await deleteTemplate.mutateAsync(id);
      showToast('Template deleted', 'success');
    } catch {
      showToast('Failed to delete', 'error');
    }
  }

  async function handleDuplicate(e: React.MouseEvent, id: number) {
    e.stopPropagation();
    try {
      await duplicateTemplate.mutateAsync(id);
      showToast('Template duplicated with all mappings', 'success');
    } catch {
      showToast('Failed to duplicate template', 'error');
    }
  }

  function openEditMeta(
    e: React.MouseEvent,
    t: {
      id: number;
      display_name?: string;
      name?: string;
      category?: string;
      description?: string;
      document_category_id?: number | null;
      mark_for_printing_default?: boolean;
      naming_convention?: string | null;
    },
  ) {
    e.stopPropagation();
    setEditMetaId(t.id);
    setEditDisplay(t.display_name || t.name || '');
    setEditCategory(t.category || '');
    setEditDesc(t.description || '');
    setEditTab('general');
    setEditCategoryId(t.document_category_id ?? '');
    setEditMarkForPrinting(t.mark_for_printing_default ?? false);
    setEditNamingConvention(t.naming_convention ?? '');
  }

  async function handleSaveMeta() {
    if (!editMetaId) return;
    try {
      await updateTemplate.mutateAsync({
        id: editMetaId,
        display_name: editDisplay,
        category: editCategory,
        description: editDesc,
        document_category_id: editCategoryId !== '' ? (editCategoryId as number) : null,
        mark_for_printing_default: editMarkForPrinting,
        naming_convention: editNamingConvention || null,
      });
      showToast('Template updated', 'success');
      setEditMetaId(null);
    } catch {
      showToast('Failed to update template', 'error');
    }
  }

  function updateMapping(fieldName: string, patch: Partial<FieldMappingConfig>) {
    setMappings((prev) => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], ...patch },
    }));
  }

  async function handleSaveMappings() {
    if (!editingId) return;
    try {
      await saveFieldMappings.mutateAsync({ templateId: editingId, mappings });
      showToast('Field mappings saved', 'success');
    } catch {
      showToast('Failed to save mappings', 'error');
    }
  }

  async function handleRescan() {
    if (!editingId) return;
    try {
      await rescanTemplate.mutateAsync(editingId);
      showToast('Fields rescanned', 'success');
    } catch {
      showToast('Rescan failed', 'error');
    }
  }

  async function handleTest() {
    if (!editingId || !testCaseRef) return;
    try {
      const { blob, filename } = await generateTest.mutateAsync({
        templateId: editingId,
        caseRef: testCaseRef,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast('Test letter generated', 'success');
    } catch {
      showToast('Test generation failed', 'error');
    }
  }

  /* ── Field count helpers ── */
  const fieldNames = Object.keys(mappings);
  const mappedCount = fieldNames.filter((n) => isMappingConfigured(mappings[n])).length;

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      {/* ── Template list ── */}
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <div>
            <h2 className={css.sectionTitle}>Draft Assistant Templates</h2>
            <p className={css.sectionSubtitle}>
              {templates.length} template{templates.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className={css.toolbar}>
            <div className={css.searchWrapper}>
              <span className={css.searchIcon}>&#128269;</span>
              <input
                className={css.searchInput}
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button className={css.btnPrimary} onClick={openAdd}>
              + New Template
            </button>
          </div>
        </div>

        {/* Category filters */}
        {categories.length > 2 && (
          <div style={{ display: 'flex', gap: 6, padding: '0 0 12px', flexWrap: 'wrap' }}>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 16,
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 500,
                  border: '1px solid',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  ...(activeCategory === cat
                    ? {
                        background: 'var(--primary-600)',
                        color: 'white',
                        borderColor: 'var(--primary-600)',
                      }
                    : {
                        background: 'var(--surface-primary)',
                        color: 'var(--text-secondary)',
                        borderColor: 'var(--border-primary)',
                      }),
                }}
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>
        )}

        {/* Template cards */}
        {filtered.length === 0 ? (
          <div className={css.emptyState}>
            <div className={css.emptyIcon}>&#128221;</div>
            <p className={css.emptyMessage}>No templates found</p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            {filtered.map((t) => {
              const isPdf = t.file_type === 'pdf';
              return (
                <div
                  key={t.id}
                  onClick={() => setEditingId(t.id)}
                  style={{
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 16,
                    background: 'var(--surface-primary)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    opacity: t.is_active === false ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--primary-500)';
                    (e.currentTarget as HTMLElement).style.boxShadow =
                      '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-primary)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 38,
                          height: 38,
                          borderRadius: 8,
                          fontSize: '1.2rem',
                          background: isPdf ? 'var(--error-50)' : 'var(--info-50)',
                        }}
                      >
                        {isPdf ? '\uD83D\uDCC4' : '\uD83D\uDCDD'}
                      </span>
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--text-primary)',
                          }}
                        >
                          {t.display_name || t.name}
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                          <span
                            style={{
                              background: isPdf ? 'var(--error-50)' : 'var(--info-50)',
                              color: isPdf ? 'var(--error-600)' : 'var(--info-600)',
                              padding: '1px 8px',
                              borderRadius: 10,
                              fontSize: '0.68rem',
                              fontWeight: 600,
                            }}
                          >
                            {(t.file_type || '').toUpperCase()}
                          </span>
                          <span
                            style={{
                              background: 'var(--success-50)',
                              color: 'var(--success-600)',
                              padding: '1px 8px',
                              borderRadius: 10,
                              fontSize: '0.68rem',
                              fontWeight: 500,
                            }}
                          >
                            {t.category || 'General'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => openEditMeta(e, t)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-tertiary)',
                        cursor: 'pointer',
                        padding: '4px 6px',
                        fontSize: '0.85rem',
                      }}
                      title="Edit name"
                    >
                      &#9998;
                    </button>
                    <button
                      onClick={(e) => handleDuplicate(e, t.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-tertiary)',
                        cursor: 'pointer',
                        padding: '4px 6px',
                        fontSize: '0.85rem',
                      }}
                      title="Duplicate"
                    >
                      &#128203;
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, t.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-tertiary)',
                        cursor: 'pointer',
                        padding: '4px 6px',
                        fontSize: '0.85rem',
                      }}
                      title="Delete"
                    >
                      &#128465;&#65039;
                    </button>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 14,
                      fontSize: '0.78rem',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    <span>
                      {t.field_count ?? 0} field{(t.field_count ?? 0) !== 1 ? 's' : ''}
                    </span>
                    {!t.all_mapped && (t.field_count ?? 0) > 0 ? (
                      <span style={{ color: 'var(--warning-600)' }}>Unmapped</span>
                    ) : (
                      <span style={{ color: 'var(--success-600)' }}>Mapped</span>
                    )}
                    <span>
                      {t.updated_at ? new Date(t.updated_at).toLocaleDateString('en-GB') : '\u2014'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit metadata modal */}
      {editMetaId !== null && (
        <div className={css.modalBackdrop} onClick={() => setEditMetaId(null)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>Edit Template</h3>
              <button className={css.modalClose} onClick={() => setEditMetaId(null)}>
                &times;
              </button>
            </div>

            {/* Tab bar */}
            <div
              style={{
                display: 'flex',
                gap: 0,
                borderBottom: '1px solid var(--border-primary)',
                padding: '0 16px',
              }}
            >
              {(['general', 'ledger'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setEditTab(tab)}
                  style={{
                    padding: '8px 16px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: editTab === tab ? 600 : 400,
                    color: editTab === tab ? 'var(--primary-600)' : 'var(--text-secondary)',
                    borderBottom:
                      editTab === tab ? '2px solid var(--primary-600)' : '2px solid transparent',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  {tab === 'general' ? '⚙️ General' : '📒 Ledger Settings'}
                </button>
              ))}
            </div>

            <div className={css.modalBody}>
              {editTab === 'general' ? (
                <>
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Display Name</label>
                    <input
                      className={css.formInput}
                      value={editDisplay}
                      onChange={(e) => setEditDisplay(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Category</label>
                    <input
                      className={css.formInput}
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      placeholder="e.g. Claim Forms, Pre-Litigation"
                    />
                  </div>
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Description</label>
                    <textarea
                      className={css.formTextarea}
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      rows={3}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Document Category</label>
                    <select
                      className={css.formSelect}
                      value={editCategoryId}
                      onChange={(e) =>
                        setEditCategoryId(e.target.value ? Number(e.target.value) : '')
                      }
                    >
                      <option value="">— None —</option>
                      {docCategories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <p style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                      Documents generated from this template will be tagged with this ledger
                      category.
                    </p>
                  </div>
                  <div className={css.formGroup}>
                    <label
                      className={css.formLabel}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        checked={editMarkForPrinting}
                        onChange={(e) => setEditMarkForPrinting(e.target.checked)}
                      />
                      Mark for printing by default
                    </label>
                    <p style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                      Newly generated documents will be added to the print queue automatically.
                    </p>
                  </div>
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Naming Convention</label>
                    <input
                      className={css.formInput}
                      value={editNamingConvention}
                      onChange={(e) => setEditNamingConvention(e.target.value)}
                      placeholder="{case_ref}_{template_name}_{date}"
                    />
                    <p style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                      Tokens: {'{case_ref}'}, {'{template_name}'}, {'{date}'}, {'{generated_by}'}
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setEditMetaId(null)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleSaveMeta}
                disabled={!editDisplay.trim() || updateTemplate.isPending}
              >
                {updateTemplate.isPending ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add new modal */}
      {showAddModal && (
        <div className={css.modalBackdrop} onClick={() => setShowAddModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>Upload New Template</h3>
              <button className={css.modalClose} onClick={() => setShowAddModal(false)}>
                &times;
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Internal Name</label>
                  <input
                    className={css.formInput}
                    value={tName}
                    onChange={(e) => setTName(e.target.value)}
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Display Name</label>
                  <input
                    className={css.formInput}
                    value={tDisplay}
                    onChange={(e) => setTDisplay(e.target.value)}
                  />
                </div>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Category</label>
                <input
                  className={css.formInput}
                  value={tCategory}
                  onChange={(e) => setTCategory(e.target.value)}
                  placeholder="e.g. Claim Forms, Pre-Litigation"
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Description</label>
                <textarea
                  className={css.formTextarea}
                  value={tDesc}
                  onChange={(e) => setTDesc(e.target.value)}
                  rows={2}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Template File (PDF or DOCX)</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx"
                  onChange={(e) => setTFile(e.target.files?.[0] || null)}
                />
              </div>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-tertiary)' }}>
                Merge fields will be auto-detected after upload. Configure field mappings by
                clicking the template card.
              </p>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={handleAdd}
                disabled={!tName || createTemplate.isPending}
              >
                {createTemplate.isPending ? 'Uploading\u2026' : 'Upload & Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mapper modal (full-screen overlay) */}
      {editingId && fullTemplate && (
        <MapperModal
          template={fullTemplate}
          mappings={mappings}
          selectedField={selectedField}
          fieldDefs={fieldDefs}
          mappedCount={mappedCount}
          totalFields={fieldNames.length}
          testCaseRef={testCaseRef}
          saving={saveFieldMappings.isPending}
          testing={generateTest.isPending}
          rescanning={rescanTemplate.isPending}
          onSelectField={setSelectedField}
          onUpdateMapping={updateMapping}
          onSave={handleSaveMappings}
          onRescan={handleRescan}
          onClose={() => {
            setEditingId(null);
          }}
          onTestRefChange={setTestCaseRef}
          onTestGenerate={handleTest}
        />
      )}
    </>
  );
}

/* ───────────────────────────────────────────────────────────────────
 *  MapperModal — Full-screen overlay matching old FE visual mapper
 *  Left: PDF canvas / DOCX preview    Right: field list + mapping config
 * ─────────────────────────────────────────────────────────────────── */

interface MapperProps {
  template: DraftTemplate;
  mappings: Record<string, FieldMappingConfig>;
  selectedField: string | null;
  fieldDefs: {
    id?: number;
    name: string;
    key: string;
    description?: string;
    category_name?: string;
    source_table?: string;
  }[];
  mappedCount: number;
  totalFields: number;
  testCaseRef: string;
  saving: boolean;
  testing: boolean;
  rescanning: boolean;
  onSelectField: (name: string | null) => void;
  onUpdateMapping: (name: string, patch: Partial<FieldMappingConfig>) => void;
  onSave: () => void;
  onRescan: () => void;
  onClose: () => void;
  onTestRefChange: (v: string) => void;
  onTestGenerate: () => void;
}

function MapperModal({
  template,
  mappings,
  selectedField,
  fieldDefs,
  mappedCount,
  totalFields,
  testCaseRef,
  saving,
  testing,
  rescanning,
  onSelectField,
  onUpdateMapping,
  onSave,
  onRescan,
  onClose,
  onTestRefChange,
  onTestGenerate,
}: MapperProps) {
  const isPdf = template.file_type === 'pdf';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '95vw',
          maxWidth: 1400,
          height: '90vh',
          borderRadius: 12,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface-primary)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px',
            borderBottom: '1px solid var(--border-primary)',
            background: 'var(--surface-secondary)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-base)',
                fontWeight: 700,
                color: 'var(--text-primary)',
              }}
            >
              {template.display_name || template.name}
            </h3>
            <span
              style={{
                background: isPdf ? '#fef2f2' : '#eff6ff',
                color: isPdf ? '#ef4444' : '#2563eb',
                padding: '2px 10px',
                borderRadius: 10,
                fontSize: '0.72rem',
                fontWeight: 600,
              }}
            >
              {(template.file_type || '').toUpperCase()}
            </span>
            <span
              style={{
                background: '#f0fdf4',
                color: '#16a34a',
                padding: '2px 10px',
                borderRadius: 10,
                fontSize: '0.72rem',
                fontWeight: 500,
              }}
            >
              {template.category || 'General'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              placeholder="Case Ref e.g. IL-100001"
              value={testCaseRef}
              onChange={(e) => onTestRefChange(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--border-primary)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                width: 180,
                background: 'var(--surface-primary)',
                color: 'var(--text-primary)',
              }}
            />
            <button
              onClick={onTestGenerate}
              disabled={!testCaseRef || testing}
              style={{
                padding: '6px 14px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
                border: '1px solid var(--border-primary)',
                background: 'var(--surface-primary)',
                color: 'var(--text-primary)',
                opacity: !testCaseRef || testing ? 0.5 : 1,
              }}
            >
              Test
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              style={{
                padding: '6px 16px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
                border: 'none',
                background: 'var(--primary-600)',
                color: 'white',
              }}
            >
              {saving ? 'Saving\u2026' : 'Save Mappings'}
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.4rem',
                cursor: 'pointer',
                color: 'var(--text-tertiary)',
                padding: '0 4px',
                lineHeight: 1,
              }}
              title="Close"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Split layout: viewer + mapping panel */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* LEFT - Document viewer */}
          <div
            style={{
              flex: 3,
              overflow: 'auto',
              background: '#374151',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: 16,
            }}
          >
            {isPdf ? (
              <PdfViewer
                templateId={template.id}
                fields={template.detected_fields ?? []}
                mappings={mappings}
                selectedField={selectedField}
                onSelectField={onSelectField}
              />
            ) : (
              <DocxViewer templateId={template.id} />
            )}
          </div>

          {/* RIGHT - Field Mappings panel */}
          <div
            style={{
              flex: 2,
              minWidth: 380,
              maxWidth: 480,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '1px solid var(--border-primary)',
              background: 'var(--surface-secondary)',
            }}
          >
            {/* Panel header */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-primary)' }}>
              <h4
                style={{
                  margin: '0 0 4px',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                }}
              >
                Field Mappings
              </h4>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', margin: '0 0 8px' }}>
                Click a field on the {isPdf ? 'PDF' : 'document'} to select it, then configure its
                mapping.
              </p>
              <div style={{ display: 'flex', gap: 12, fontSize: '0.72rem' }}>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      background: 'rgba(245,158,11,0.35)',
                      border: '2px solid #f59e0b',
                      borderRadius: 2,
                      verticalAlign: 'middle',
                    }}
                  />{' '}
                  Unmapped
                </span>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      background: 'rgba(34,197,94,0.25)',
                      border: '2px solid #22c55e',
                      borderRadius: 2,
                      verticalAlign: 'middle',
                    }}
                  />{' '}
                  Mapped
                </span>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      background: 'rgba(139,92,246,0.3)',
                      border: '2px solid #8b5cf6',
                      borderRadius: 2,
                      verticalAlign: 'middle',
                    }}
                  />{' '}
                  Selected
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 8,
                }}
              >
                <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  {mappedCount}/{totalFields} mapped
                </span>
                <button
                  onClick={onRescan}
                  disabled={rescanning}
                  style={{
                    padding: '3px 10px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.72rem',
                    border: '1px solid var(--border-primary)',
                    background: 'var(--surface-primary)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  {rescanning ? 'Scanning\u2026' : 'Rescan Fields'}
                </button>
              </div>
            </div>

            {/* Field list (scrollable) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
              {Object.keys(mappings).length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 30,
                    color: 'var(--text-tertiary)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  No fields detected. Click "Rescan Fields" to auto-detect merge fields.
                </div>
              ) : (
                Object.entries(mappings).map(([name, mapping]) => {
                  const isSelected = selectedField === name;
                  const mapped = isMappingConfigured(mapping);
                  const detectedField = template.detected_fields?.find((f) => f.name === name);
                  return (
                    <FieldRow
                      key={name}
                      name={name}
                      mapping={mapping}
                      isSelected={isSelected}
                      isMapped={mapped}
                      fieldDefs={fieldDefs}
                      detectedField={detectedField}
                      onSelect={() => onSelectField(isSelected ? null : name)}
                      onUpdate={(patch) => onUpdateMapping(name, patch)}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────
 *  PdfViewer — renders PDF via PDF.js with
 *  clickable field overlays
 * ─────────────────────────────────────────── */

interface PdfViewerProps {
  templateId: number;
  fields: DetectedField[];
  mappings: Record<string, FieldMappingConfig>;
  selectedField: string | null;
  onSelectField: (name: string) => void;
}

function PdfViewer({ templateId, fields, mappings, selectedField, onSelectField }: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const SCALE = 1.2;

  const renderPdf = useCallback(async () => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = '';
    setLoaded(false);
    setError(null);

    // Dynamically load PDF.js from CDN if not already present
    const pdfWindow = window as unknown as { pdfjsLib?: PdfjsLib };
    if (!pdfWindow.pdfjsLib) {
      try {
        await loadScript('https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js');

        const lib = (window as unknown as { pdfjsLib?: PdfjsLib }).pdfjsLib;
        if (lib?.GlobalWorkerOptions)
          lib.GlobalWorkerOptions.workerSrc =
            'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
      } catch {
        setError('Failed to load PDF.js library');
        return;
      }
    }

    const pdfjsLib = (window as unknown as { pdfjsLib?: PdfjsLib }).pdfjsLib;
    if (!pdfjsLib) {
      setError('PDF.js library failed to load');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const resp = await fetch(
        CONFIG.BACKEND_BASE_URL + '/api/admin/draft-templates/' + templateId + '/file',
        {
          headers: token ? { Authorization: 'Bearer ' + token } : {},
        },
      );
      if (!resp.ok) throw new Error('Failed to load PDF');
      const pdfData = await resp.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;

      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const viewport = page.getViewport({ scale: SCALE });

        const pageDiv = document.createElement('div');
        pageDiv.style.cssText =
          'position:relative; margin-bottom:16px; box-shadow:0 2px 12px rgba(0,0,0,0.3);';

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.display = 'block';
        pageDiv.appendChild(canvas);

        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;

        // Overlay field rectangles for this page
        const pageFields = fields.filter((f) => f.page === p - 1);
        pageFields.forEach((f) => {
          if (!f.rect) return;
          const r = f.rect;
          const ph = f.pageHeight || viewport.height / SCALE;

          const screenX = r.x * SCALE;
          const screenY = (ph - r.y - r.height) * SCALE;
          const w = r.width * SCALE;
          const h = r.height * SCALE;

          const overlay = document.createElement('div');
          overlay.dataset.fieldName = f.name;
          overlay.style.cssText =
            'position:absolute; left:' +
            screenX +
            'px; top:' +
            screenY +
            'px; width:' +
            w +
            'px; height:' +
            h +
            'px; border:2px solid #f59e0b; background:rgba(245,158,11,0.2); cursor:pointer; transition:all 0.15s; z-index:10; border-radius:2px; box-sizing:border-box;';
          overlay.title = f.name;
          overlay.addEventListener('click', () => onSelectField(f.name));
          pageDiv.appendChild(overlay);
        });

        containerRef.current?.appendChild(pageDiv);
      }
      setLoaded(true);
    } catch (err) {
      setError((err as Error).message || 'Failed to render PDF');
    }
  }, [templateId, SCALE]);

  // Render on mount
  useEffect(() => {
    renderPdf();
  }, [renderPdf]);

  // Update overlay colours when selection / mappings change (without re-rendering canvas)
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.querySelectorAll<HTMLElement>('[data-field-name]').forEach((el) => {
      const name = el.dataset.fieldName!;
      const mapped = isMappingConfigured(mappings[name] || { source_type: 'database' });
      const selected = selectedField === name;
      el.style.borderColor = selected ? '#8b5cf6' : mapped ? '#22c55e' : '#f59e0b';
      el.style.background = selected
        ? 'rgba(139,92,246,0.3)'
        : mapped
          ? 'rgba(34,197,94,0.15)'
          : 'rgba(245,158,11,0.2)';
    });
  }, [selectedField, mappings]);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#fbbf24', maxWidth: 400 }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>&#128196;</div>
        <p>{error}</p>
        <p style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
          Use the field list on the right to configure mappings.
        </p>
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading PDF&#8230;</div>
      )}
      <div ref={containerRef} />
    </>
  );
}

/** Helper to dynamically load a script */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src="' + src + '"]')) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Script load failed: ' + src));
    document.head.appendChild(s);
  });
}

/* ───────────────────────────────────────────
 *  DocxViewer — renders the raw DOCX file
 *  using docx-preview for a faithful preview
 *  with letterhead, page breaks, styles, etc.
 * ─────────────────────────────────────────── */

function DocxViewer({ templateId }: { templateId: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Create a dedicated <style> element in <head> for docx-preview's CSS
    // (numbering counters, list styles, etc. must live at document level)
    if (!styleRef.current) {
      styleRef.current = document.createElement('style');
      styleRef.current.setAttribute('data-docx-preview', 'true');
      document.head.appendChild(styleRef.current);
    }

    async function render() {
      setLoading(true);
      setError(null);
      try {
        const token = sessionStorage.getItem('token');
        const resp = await fetch(
          CONFIG.BACKEND_BASE_URL + '/api/admin/draft-templates/' + templateId + '/file',
          { headers: token ? { Authorization: 'Bearer ' + token } : {} },
        );
        if (!resp.ok) throw new Error('Failed to load DOCX file');
        const blob = await resp.blob();
        if (cancelled || !containerRef.current) return;

        containerRef.current.innerHTML = '';
        await renderAsync(blob, containerRef.current, styleRef.current!, {
          className: 'docx',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          experimental: true,
          trimXmlDeclaration: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });

        if (cancelled || !containerRef.current) return;

        // ── Post-process: fix page numbers ──
        // docx-preview renders PAGE / NUMPAGES fields with cached values
        // from the XML (often the same number on every page).
        // Walk every page-section's footer and header, replacing
        // bare-number text nodes with sequential page / total values.
        const wrapper = containerRef.current.querySelector('.docx-wrapper');
        if (wrapper) {
          const pages = wrapper.querySelectorAll('section.docx');
          const totalPages = pages.length;
          pages.forEach((page, idx) => {
            const hfElements = page.querySelectorAll('footer, header');
            hfElements.forEach((hf) => {
              const walker = document.createTreeWalker(hf, NodeFilter.SHOW_TEXT);
              const numberNodes: { node: Node; prevText: string; nextText: string }[] = [];
              let node: Node | null;
              while ((node = walker.nextNode())) {
                const txt = node.textContent?.trim() ?? '';
                if (/^\d{1,4}$/.test(txt)) {
                  // Gather sibling text for context (to distinguish "Page X" from "of Y")
                  const prev = node.previousSibling?.textContent?.trim().toLowerCase() ?? '';
                  const next = node.nextSibling?.textContent?.trim().toLowerCase() ?? '';
                  numberNodes.push({ node, prevText: prev, nextText: next });
                }
              }
              // If there are exactly 2 number nodes, assume first is PAGE, second is NUMPAGES
              // Otherwise replace all with current page number
              if (numberNodes.length === 2) {
                numberNodes[0].node.textContent = numberNodes[0].node.textContent!.replace(
                  /\d+/,
                  String(idx + 1),
                );
                numberNodes[1].node.textContent = numberNodes[1].node.textContent!.replace(
                  /\d+/,
                  String(totalPages),
                );
              } else {
                numberNodes.forEach(({ node: n }) => {
                  n.textContent = n.textContent!.replace(/\d+/, String(idx + 1));
                });
              }
            });
          });
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message || 'Preview failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    render();
    return () => {
      cancelled = true;
      // Clean up the <style> element when unmounting
      if (styleRef.current && styleRef.current.parentNode) {
        styleRef.current.parentNode.removeChild(styleRef.current);
        styleRef.current = null;
      }
    };
  }, [templateId]);

  return (
    <>
      {/* Scope docx-preview page styling */}
      <style>{`
        .docx-preview-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          width: 100%;
          padding: 8px 0;
        }
        .docx-preview-wrapper > .docx-wrapper {
          background: transparent !important;
          padding: 20px 0 !important;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }
        .docx-preview-wrapper > .docx-wrapper > section.docx {
          box-shadow: 0 2px 12px rgba(0,0,0,0.12) !important;
          border-radius: 6px;
          margin-bottom: 0 !important;
        }
        /* Ensure list numbering is visible */
        .docx-preview-wrapper ol, .docx-preview-wrapper ul {
          list-style-position: inside;
        }
      `}</style>
      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>
          Loading preview&#8230;
        </div>
      )}
      {error && (
        <div style={{ padding: 40, textAlign: 'center', color: '#fbbf24', maxWidth: 400 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>&#128196;</div>
          <p>{error}</p>
        </div>
      )}
      <div ref={containerRef} className="docx-preview-wrapper" />
    </>
  );
}

/* ───────────────────────────────────────────
 *  FieldRow — single field in the right panel
 *  with expandable mapping config
 * ─────────────────────────────────────────── */

interface FieldRowProps {
  name: string;
  mapping: FieldMappingConfig;
  isSelected: boolean;
  isMapped: boolean;
  fieldDefs: {
    id?: number;
    name: string;
    key: string;
    description?: string;
    category_name?: string;
    source_table?: string;
  }[];
  detectedField?: DetectedField;
  onSelect: () => void;
  onUpdate: (patch: Partial<FieldMappingConfig>) => void;
}

function FieldRow({
  name,
  mapping,
  isSelected,
  isMapped,
  fieldDefs,
  detectedField,
  onSelect,
  onUpdate,
}: FieldRowProps) {
  const statusColor = isMapped ? '#22c55e' : '#f59e0b';

  return (
    <div
      onClick={onSelect}
      style={{
        padding: '10px 12px',
        marginBottom: 6,
        border: '1px solid ' + (isSelected ? '#8b5cf6' : 'var(--border-primary)'),
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.15s',
        background: isSelected ? 'rgba(139,92,246,0.06)' : 'var(--surface-primary)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <code
          style={{
            background: 'var(--surface-tertiary)',
            padding: '2px 6px',
            borderRadius: 4,
            fontSize: '0.78rem',
            color: '#7c3aed',
          }}
        >
          {name}
        </code>
        <span style={{ color: statusColor, fontSize: '0.75rem' }}>
          {isMapped ? '\u25CF Mapped' : '\u25CF Unmapped'}
        </span>
      </div>

      {/* Expanded config */}
      {isSelected && (
        <div
          style={{ marginTop: 10, borderTop: '1px solid var(--border-primary)', paddingTop: 10 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Source type */}
          <div style={{ marginBottom: 8 }}>
            <label
              style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                display: 'block',
                marginBottom: 3,
              }}
            >
              Source Type
            </label>
            <select
              value={mapping.source_type}
              onChange={(e) =>
                onUpdate({ source_type: e.target.value as FieldMappingConfig['source_type'] })
              }
              style={{
                width: '100%',
                fontSize: '0.75rem',
                padding: '5px 8px',
                border: '1px solid var(--border-primary)',
                borderRadius: 4,
                background: 'var(--surface-primary)',
                color: 'var(--text-primary)',
              }}
            >
              {SOURCE_TYPES.map((st) => (
                <option key={st.value} value={st.value}>
                  {st.label}
                </option>
              ))}
            </select>
          </div>

          {/* Dynamic inputs based on source type */}
          {mapping.source_type === 'database' && (
            <div style={{ marginBottom: 8 }}>
              <label
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  display: 'block',
                  marginBottom: 3,
                }}
              >
                Database Field
              </label>
              <select
                value={mapping.field_key || ''}
                onChange={(e) => {
                  const def = fieldDefs.find((d) => d.key === e.target.value);
                  onUpdate({
                    field_key: e.target.value,
                    field_name: def?.name,
                    field_definition_id: def?.id,
                    source_table: def?.source_table,
                  });
                }}
                style={{
                  width: '100%',
                  fontSize: '0.75rem',
                  padding: '5px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 4,
                  background: 'var(--surface-primary)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="">-- Select field --</option>
                {fieldDefs.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.name}
                    {d.category_name ? ' (' + d.category_name + ')' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mapping.source_type === 'system' && (
            <div style={{ marginBottom: 8 }}>
              <label
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  display: 'block',
                  marginBottom: 3,
                }}
              >
                System Field
              </label>
              <select
                value={mapping.system_field || ''}
                onChange={(e) => onUpdate({ system_field: e.target.value })}
                style={{
                  width: '100%',
                  fontSize: '0.75rem',
                  padding: '5px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 4,
                  background: 'var(--surface-primary)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="">-- Select system field --</option>
                {SYSTEM_FIELDS.map((sf) => (
                  <option key={sf.value} value={sf.value}>
                    {sf.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mapping.source_type === 'custom_sql' && (
            <div style={{ marginBottom: 8 }}>
              <label
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  display: 'block',
                  marginBottom: 3,
                }}
              >
                Custom SQL Expression
              </label>
              <textarea
                value={mapping.custom_sql || ''}
                onChange={(e) => onUpdate({ custom_sql: e.target.value })}
                rows={3}
                placeholder="e.g. COALESCE(dao.claim_form_value, 0)"
                style={{
                  width: '100%',
                  fontSize: '0.72rem',
                  padding: '5px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 4,
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  background: 'var(--surface-primary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          )}

          {mapping.source_type === 'conditional' && (
            <div style={{ marginBottom: 8 }}>
              <label
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  display: 'block',
                  marginBottom: 3,
                }}
              >
                Conditional Rules (JSON)
              </label>
              <textarea
                value={
                  mapping.rules
                    ? JSON.stringify(
                        { rules: mapping.rules, fallback: mapping.fallback || '' },
                        null,
                        2,
                      )
                    : ''
                }
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    onUpdate({ rules: parsed.rules, fallback: parsed.fallback });
                  } catch {
                    /* wait until valid JSON */
                  }
                }}
                rows={4}
                placeholder='{"rules":[{"conditions":{"col":"val"},"output":"..."}],"fallback":""}'
                style={{
                  width: '100%',
                  fontSize: '0.7rem',
                  padding: '5px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 4,
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  background: 'var(--surface-primary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          )}

          {mapping.source_type === 'user_context' && (
            <div style={{ marginBottom: 8 }}>
              <label
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  display: 'block',
                  marginBottom: 3,
                }}
              >
                User Context Field
              </label>
              <select
                value={mapping.user_field || ''}
                onChange={(e) => onUpdate({ user_field: e.target.value })}
                style={{
                  width: '100%',
                  fontSize: '0.75rem',
                  padding: '5px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 4,
                  background: 'var(--surface-primary)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="">-- Select user field --</option>
                {USER_CONTEXT_FIELDS.map((uf) => (
                  <option key={uf.value} value={uf.value}>
                    {uf.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mapping.source_type === 'manual_input' && (
            <>
              <div style={{ marginBottom: 4 }}>
                <label
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    display: 'block',
                    marginBottom: 3,
                  }}
                >
                  Prompt Label
                </label>
                <input
                  value={mapping.manual_label || ''}
                  onChange={(e) => onUpdate({ manual_label: e.target.value })}
                  placeholder="e.g. Court Name"
                  style={{
                    width: '100%',
                    fontSize: '0.75rem',
                    padding: '5px 8px',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                    background: 'var(--surface-primary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    display: 'block',
                    marginBottom: 3,
                  }}
                >
                  Default Value
                </label>
                <input
                  value={mapping.default_value || ''}
                  onChange={(e) => onUpdate({ default_value: e.target.value })}
                  placeholder="Optional default"
                  style={{
                    width: '100%',
                    fontSize: '0.75rem',
                    padding: '5px 8px',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                    background: 'var(--surface-primary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </>
          )}

          {mapping.source_type === 'ignore' && (
            <div
              style={{
                fontSize: '0.72rem',
                color: 'var(--text-tertiary)',
                padding: '6px 0',
                fontStyle: 'italic',
              }}
            >
              This field will be left blank intentionally.
            </div>
          )}

          {/* Format picker (for non-ignore) */}
          {mapping.source_type !== 'ignore' && (
            <div>
              <label
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  display: 'block',
                  marginBottom: 3,
                }}
              >
                Format
              </label>
              <select
                value={mapping.format || 'none'}
                onChange={(e) => onUpdate({ format: e.target.value })}
                style={{
                  width: '100%',
                  fontSize: '0.75rem',
                  padding: '5px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 4,
                  background: 'var(--surface-primary)',
                  color: 'var(--text-primary)',
                }}
              >
                {FORMAT_OPTIONS.map((fo) => (
                  <option key={fo.value} value={fo.value}>
                    {fo.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Checkbox field config (if detected field is a checkbox) */}
          {detectedField?.type === 'checkbox' && (
            <div
              style={{
                marginTop: 12,
                padding: '8px 0',
                borderTop: '1px solid var(--border-primary)',
                paddingTop: 10,
              }}
            >
              <label
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ☑ Checkbox State
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    // Toggle: if already checked, clear it; otherwise set to checked
                    if (mapping.checkbox_value === true) {
                      onUpdate({ checkbox_value: undefined });
                    } else {
                      onUpdate({ checkbox_value: true });
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    borderRadius: 4,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    border:
                      mapping.checkbox_value === true
                        ? '2px solid #8b5cf6'
                        : '1px solid var(--border-primary)',
                    background:
                      mapping.checkbox_value === true
                        ? 'rgba(139,92,246,0.1)'
                        : 'var(--surface-primary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  ✓ Checked
                </button>
                <button
                  onClick={() => {
                    // Toggle: if already unchecked, clear it; otherwise set to unchecked
                    if (mapping.checkbox_value === false) {
                      onUpdate({ checkbox_value: undefined });
                    } else {
                      onUpdate({ checkbox_value: false });
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    borderRadius: 4,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    border:
                      mapping.checkbox_value === false
                        ? '2px solid #8b5cf6'
                        : '1px solid var(--border-primary)',
                    background:
                      mapping.checkbox_value === false
                        ? 'rgba(139,92,246,0.1)'
                        : 'var(--surface-primary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  ✗ Unchecked
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
